import i18next from 'i18next';
import { toast } from 'sonner';
import { toFormTaskAssignPayload } from '@repo/contracts';
import { getHttpErrorStatus } from '@/lib/get-http-error-status.js';
import { useFormTaskStore } from '@/store/form-task.store.js';

export class FormTaskIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    async getAll(params = {}) {
        return this.service.getAll(params);
    }

    async getById(id) {
        return this.service.getById(id);
    }

    async getWaiting(params = {}) {
        return this.service.getWaiting(params);
    }

    async getMyTasks(params = {}) {
        return this.service.getMyTasks(params);
    }

    async getMyWorkInbox(params = {}) {
        return this.service.getMyWorkInbox(params);
    }

    async fetchTasks(params = {}) {
        useFormTaskStore.setState({ loading: true, error: null });
        try {
            const response = await this.service.getAll(params);
            useFormTaskStore.setState({
                tasks: response.data || [],
                totalCount: response.total || 0,
                loading: false,
            });
            return response;
        } catch (error) {
            const status = getHttpErrorStatus(error);
            useFormTaskStore.setState({
                error: status === 401 ? null : (error.message || 'An error occurred while fetching tasks'),
                loading: false,
            });
        }
    }

    async fetchWaitingTasks(params = {}) {
        useFormTaskStore.setState({ loading: true, error: null });
        try {
            const response = await this.service.getWaiting(params);
            useFormTaskStore.setState({
                waitingTasks: response.data || [],
                waitingTotalCount: response.total || 0,
                loading: false,
            });
            return response;
        } catch (error) {
            const status = getHttpErrorStatus(error);
            useFormTaskStore.setState({
                error: status === 401 ? null : (error.message || 'An error occurred while fetching waiting tasks'),
                loading: false,
            });
        }
    }

    async fetchMyTasks(params = {}) {
        useFormTaskStore.setState({ loading: true, error: null });
        try {
            const response = await this.service.getMyTasks(params);
            useFormTaskStore.setState({
                myTasks: response.data || [],
                myTotalCount: response.total || 0,
                loading: false,
            });
            return response;
        } catch (error) {
            const status = getHttpErrorStatus(error);
            useFormTaskStore.setState({
                error: status === 401 ? null : (error.message || 'An error occurred while fetching my tasks'),
                loading: false,
            });
        }
    }

    async fetchMyWorkInbox(params = {}) {
        useFormTaskStore.setState({ loading: true, error: null });
        try {
            const response = await this.service.getMyWorkInbox(params);
            useFormTaskStore.setState({
                myWorkInbox: response.data || [],
                myWorkInboxTotal: response.total ?? response.data?.length ?? 0,
                loading: false,
            });
            return response;
        } catch (error) {
            const status = getHttpErrorStatus(error);
            useFormTaskStore.setState({
                error: status === 401 ? null : (error.message || 'An error occurred while loading the task inbox'),
                loading: false,
            });
        }
    }

    async fetchTaskById(id) {
        useFormTaskStore.setState({ loading: true, error: null });
        try {
            const response = await this.service.getById(id);
            useFormTaskStore.setState({ selectedTask: response.data, loading: false });
            return response;
        } catch (error) {
            useFormTaskStore.setState({
                error: error.message || 'An error occurred while fetching task details',
                loading: false,
            });
            throw error;
        }
    }

    async assignTask(taskId, data) {
        useFormTaskStore.setState({ loading: true, error: null });
        try {
            const response = await this.service.assign(taskId, toFormTaskAssignPayload(data));
            useFormTaskStore.setState((state) => ({
                waitingTasks: state.waitingTasks.filter((t) => t._id !== taskId),
                waitingTotalCount: Math.max(0, state.waitingTotalCount - 1),
                loading: false,
            }));
            return response;
        } catch (error) {
            useFormTaskStore.setState({
                error: error.message || 'An error occurred while assigning the task',
                loading: false,
            });
            throw error;
        }
    }

    async completeTask(taskId, data) {
        useFormTaskStore.setState({ loading: true, error: null });
        try {
            const response = await this.service.complete(taskId, data);
            useFormTaskStore.setState((state) => ({
                myTasks: state.myTasks.map((t) => (t._id === taskId ? response.data : t)),
                loading: false,
            }));
            return response;
        } catch (error) {
            useFormTaskStore.setState({
                error: error.message || 'An error occurred while completing the task',
                loading: false,
            });
            throw error;
        }
    }

    async saveSurveyDraft(taskId, data) {
        try {
            const res = await this.service.saveSurveyDraft(taskId, data);
            toast.success(i18next.t('integrationToasts:draftSaved', { defaultValue: 'Draft saved' }));
            return { success: true, data: res.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    async submitSurveyResponse(taskId, data) {
        try {
            const res = await this.service.submitSurveyResponse(taskId, data);
            toast.success(i18next.t('integrationToasts:surveySubmitted', { defaultValue: 'Survey submitted' }));
            return { success: true, data: res.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    async create(data) {
        return this.service.create(data);
    }

    async update(id, data) {
        return this.service.update(id, data);
    }

    async assign(taskId, data) {
        return this.service.assign(taskId, toFormTaskAssignPayload(data));
    }

    async complete(taskId, data) {
        return this.service.complete(taskId, data);
    }

    async delete(id) {
        return this.service.delete(id);
    }

    _getErrorMessage(error) {
        return error?.response?.data?.error || error?.message || 'An unexpected error occurred';
    }
}
